// ALTER TABLE structure extractor. Walks the leading clause from the source
// buffer, reads the qualified table name, then splits the remaining clause
// list on top-level commas. Each clause is classified as one of the v1 narrow
// shapes (add-column / drop-column / rename-column) or — for anything outside
// that surface — as `opaque` so the round-trip preserves it byte-for-byte.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor —
//      ALTER TABLE / Index forms.

import type { ParseOptions, QualifiedName, StatementInfo } from '../../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import { readQualifiedName } from '../../scope/identifiers';
import type { AlterTableClause, DdlStructure, ParsedBuffer } from '../../types';

import { findTopLevelCommas } from '../../structure/scanner';
import { skipWsCommentsLocal, tryConsumeKeyword } from './shared';
import { classifyAlterTableClause } from './alterTable.clause';

interface AlterHeader {
  readonly table: QualifiedName;
  /** Offset just past the qualified table name (start of the clause list). */
  readonly afterName: number;
}

function placeholderQn(): QualifiedName {
  return { name: '', raw: '', quoted: false, foldedName: '' };
}

/** Skip ALTER TABLE [IF EXISTS] and read the qualified table name. Returns
 *  null if any required token is missing or out of order. */
function readAlterHeader(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): AlterHeader | null {
  let i = skipWsCommentsLocal(source, start, end, options);
  const afterAlter = tryConsumeKeyword(source, i, end, options, 'ALTER');
  if (afterAlter === i) return null;
  i = skipWsCommentsLocal(source, afterAlter, end, options);
  const afterTable = tryConsumeKeyword(source, i, end, options, 'TABLE');
  if (afterTable === i) return null;
  i = skipWsCommentsLocal(source, afterTable, end, options);

  // Optional `IF EXISTS` (Postgres / MySQL / Oracle 23c).
  const afterIf = tryConsumeKeyword(source, i, end, options, 'IF');
  if (afterIf !== i) {
    let j = skipWsCommentsLocal(source, afterIf, end, options);
    j = tryConsumeKeyword(source, j, end, options, 'EXISTS');
    j = skipWsCommentsLocal(source, j, end, options);
    i = j;
  }

  // Optional `ONLY` (Postgres) — applies recursion semantics, not structural.
  const afterOnly = tryConsumeKeyword(source, i, end, options, 'ONLY');
  if (afterOnly !== i) i = skipWsCommentsLocal(source, afterOnly, end, options);

  const qn = readQualifiedName(source, i, end, options);
  if (!qn) return null;
  return { table: qn.qualified, afterName: qn.range.endOffset };
}

/** Public entry — extract an `alter-table` `DdlStructure` arm from the
 *  given `StatementInfo` range inside `buffer.parsedDocument.source`. */
export function extractAlterTable(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const { source, options } = buffer.parsedDocument;
  const lineIdx = buildLineIndex(source);
  const stmtStart = statementInfo.range.startOffset;
  const stmtEnd = statementInfo.range.endOffset;

  const header = readAlterHeader(source, options, stmtStart, stmtEnd);
  if (!header) {
    return {
      kind: 'alter-table',
      range: statementInfo.range,
      table: placeholderQn(),
      clauses: [
        { kind: 'opaque', range: rangeFromOffsets(lineIdx, stmtStart, stmtEnd) },
      ],
    };
  }

  // Trim trailing semicolon-only ranges so the last clause's range doesn't
  // swallow a terminating `;` that StatementInfo may include.
  const clauseEnd = trimTrailingSemicolon(source, header.afterName, stmtEnd);
  const clauseStart = skipWsCommentsLocal(source, header.afterName, clauseEnd, options);

  const clauses: AlterTableClause[] = [];
  if (clauseStart >= clauseEnd) {
    return {
      kind: 'alter-table',
      range: statementInfo.range,
      table: header.table,
      clauses,
    };
  }

  const commas = findTopLevelCommas(source, clauseStart, clauseEnd, options);
  let segStart = clauseStart;
  for (const c of commas) {
    pushClause(source, options, lineIdx, segStart, c, clauses);
    segStart = c + 1;
  }
  pushClause(source, options, lineIdx, segStart, clauseEnd, clauses);

  return {
    kind: 'alter-table',
    range: statementInfo.range,
    table: header.table,
    clauses,
  };
}

function pushClause(
  source: string,
  options: ParseOptions,
  lineIdx: ReturnType<typeof buildLineIndex>,
  segStart: number,
  segEnd: number,
  out: AlterTableClause[],
): void {
  const trimmedStart = skipWsCommentsLocal(source, segStart, segEnd, options);
  let trimmedEnd = segEnd;
  while (trimmedEnd > trimmedStart) {
    const ch = source[trimmedEnd - 1];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { trimmedEnd--; continue; }
    break;
  }
  if (trimmedStart >= trimmedEnd) return;
  const range = rangeFromOffsets(lineIdx, trimmedStart, trimmedEnd);
  const clause = classifyAlterTableClause(source, options, trimmedStart, trimmedEnd, range);
  out.push(clause);
}

function trimTrailingSemicolon(source: string, start: number, end: number): number {
  let e = end;
  while (e > start) {
    const ch = source[e - 1];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === ';') { e--; continue; }
    break;
  }
  return e;
}
